import { useMemo, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Group,
  MantineTheme,
  Paper,
  Progress,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
} from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { financialsApi } from '../../api/financials';
import { getComponentProjectDisplayName } from '../../config/componentProjects';
import { useGroup } from '../../contexts/GroupContext';
import { ComponentProjectFinancials } from '../../types/financials';
import {
  buildSubawardeeLabelLookup,
  getSubawardeeDisplayLabel,
} from '../../utils/adminSubawardeeUtils';
import {
  GivebackAtRiskOverviewCard,
  nameGivebackAtRiskSubawardees,
  type GivebackAtRiskSummary,
  summarizeGivebackAtRisk,
  summarizeProjectGivebackAtRisk,
} from './GivebackRiskCard';

function isFiniteNumber(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n);
}

const formatCurrency = (n: number): string => {
  if (!isFiniteNumber(n)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);
};

function pctOfTarget(actual: number, target: number): number {
  if (target <= 0) return 0;
  return Math.round((actual / target) * 100);
}

function sumNullable(values: Array<number | null | undefined>): number | null {
  const present = values.filter(isFiniteNumber);
  if (present.length === 0) return null;
  return present.reduce((sum, v) => sum + v, 0);
}

function projectCardPaperSx(theme: MantineTheme) {
  return {
    border: theme.colorScheme === 'dark' ? `1px solid ${theme.colors.dark[4]}` : 'none',
    boxShadow:
      theme.colorScheme === 'dark'
        ? '0 0 12px rgba(255, 255, 255, 0.1)'
        : theme.shadows.md,
    '@media (max-width: 768px)': {
      margin: theme.spacing.sm,
    },
  };
}

/** Metric identity colors — status (over/met) still overrides when applicable. */
export type SpendMetricTone = 'federal' | 'tcf' | 'match';

const SPEND_METRIC_COLORS: Record<SpendMetricTone, string> = {
  federal: 'blue',
  tcf: 'violet',
  match: 'cyan',
};

function progressColor(
  tone: SpendMetricTone,
  overTarget: boolean,
  pct: number | null
): string {
  if (overTarget) return 'orange';
  if (pct !== null && pct >= 100) return 'green';
  return SPEND_METRIC_COLORS[tone];
}

export function hasProgramBudgets(financials: ComponentProjectFinancials): boolean {
  return (
    isFiniteNumber(financials.federalBudget) ||
    isFiniteNumber(financials.tcfMatchBudget) ||
    isFiniteNumber(financials.subawardeeMatchBudget)
  );
}

/** Card tile for program / component-project spend overview. */
export function ProgramSpendCard({
  label,
  value,
  target,
  emptyLabel,
  asOf,
  action,
  tone = 'federal',
}: {
  label: string;
  value: number | null;
  target: number | null;
  emptyLabel?: string;
  asOf?: string | null;
  action?: ReactNode;
  tone?: SpendMetricTone;
}) {
  const hasValue = isFiniteNumber(value);
  const hasTarget = isFiniteNumber(target);
  const pct = hasValue && hasTarget ? pctOfTarget(value, target) : null;
  const barPct = pct === null ? 0 : Math.min(pct, 100);
  const overTarget = hasValue && hasTarget && value > target;

  return (
    <Box
      p="md"
      sx={(theme) => ({
        borderRadius: theme.radius.md,
        backgroundColor:
          theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
        border: `1px solid ${
          theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[2]
        }`,
        height: '100%',
      })}
    >
      <Stack spacing="sm">
        <Group position="apart" align="flex-start" noWrap>
          <Text size="md" weight={700}>
            {label}
          </Text>
          {action}
        </Group>

        {hasValue ? (
          <Text
            weight={700}
            sx={{
              fontSize: 28,
              lineHeight: 1.15,
              letterSpacing: '-0.02em',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {formatCurrency(value)}
          </Text>
        ) : (
          <Text color="dimmed" weight={600} sx={{ fontSize: 28, lineHeight: 1.15 }}>
            —
          </Text>
        )}

        {hasValue && asOf ? (
          <Text size="xs" color="dimmed">
            As of {asOf}
          </Text>
        ) : !hasValue && emptyLabel ? (
          <Text size="xs" color="dimmed">
            {emptyLabel}
          </Text>
        ) : null}

        {hasTarget ? (
          <Stack spacing={6} mt={4}>
            <Progress
              value={hasValue ? barPct : 0}
              size="md"
              radius="xl"
              color={progressColor(tone, overTarget, pct)}
              aria-label={`${label} target: ${pct ?? 0}%`}
            />
            <Group position="apart" noWrap spacing="xs">
              <Text size="sm" weight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {hasValue ? `${pct}% of budget` : '—'}
              </Text>
              <Text size="xs" color="dimmed" ta="right">
                {overTarget && hasValue
                  ? `${formatCurrency(value - target)} over budget`
                  : formatCurrency(target)}
              </Text>
            </Group>
          </Stack>
        ) : null}
      </Stack>
    </Box>
  );
}

type SpendOverviewRollup = {
  federalSpendToDate: number | null;
  matchSpendToDate: number | null;
  tcfMatchToDate: number | null;
  federalBudget: number | null;
  tcfMatchBudget: number | null;
  subawardeeMatchBudget: number | null;
  federalTarget: number | null;
  matchTarget: number | null;
  atRisk: GivebackAtRiskSummary;
  hasProgram: boolean;
};

function buildAllProjectsRollup(
  projectFinancials: ComponentProjectFinancials[]
): SpendOverviewRollup {
  const hasProgram = projectFinancials.some(hasProgramBudgets);
  return {
    federalSpendToDate: sumNullable(projectFinancials.map((p) => p.federalSpendToDate)),
    matchSpendToDate: sumNullable(projectFinancials.map((p) => p.matchSpendToDate)),
    tcfMatchToDate: sumNullable(projectFinancials.map((p) => p.tcfMatchToDate)),
    federalBudget: sumNullable(projectFinancials.map((p) => p.federalBudget)),
    tcfMatchBudget: sumNullable(projectFinancials.map((p) => p.tcfMatchBudget)),
    subawardeeMatchBudget: sumNullable(
      projectFinancials.map((p) => p.subawardeeMatchBudget)
    ),
    federalTarget: sumNullable(projectFinancials.map((p) => p.federalTarget)),
    matchTarget: sumNullable(projectFinancials.map((p) => p.matchTarget)),
    atRisk: summarizeProjectGivebackAtRisk(projectFinancials),
    hasProgram,
  };
}

function buildProjectSpendRollup(
  financials: ComponentProjectFinancials
): SpendOverviewRollup {
  return {
    federalSpendToDate: financials.federalSpendToDate,
    matchSpendToDate: financials.matchSpendToDate,
    tcfMatchToDate: financials.tcfMatchToDate,
    federalBudget: financials.federalBudget,
    tcfMatchBudget: financials.tcfMatchBudget,
    subawardeeMatchBudget: financials.subawardeeMatchBudget,
    federalTarget: financials.federalTarget,
    matchTarget: financials.matchTarget,
    atRisk: summarizeGivebackAtRisk(financials.items),
    hasProgram: hasProgramBudgets(financials),
  };
}

export type AllProjectsSpendOverviewProps = {
  /**
   * When set, show spend for that component project only.
   * When omitted, show consortium-wide rollup across all projects.
   */
  componentProjectId?: string | null;
  /** When false, skip fetching and render nothing. */
  enabled?: boolean;
  /**
   * When false, omit the outer card chrome (title, subtitle, and Paper wrapper)
   * — e.g. dashboard already has a SectionHeader and only needs the spend tiles.
   */
  showTitle?: boolean;
};

/**
 * Spend tiles (federal / TCF / subawardee + giveback exposure) for all projects
 * or a single component project. Shares the admin financials React Query cache.
 */
export function AllProjectsSpendOverview({
  componentProjectId = null,
  enabled = true,
  showTitle = true,
}: AllProjectsSpendOverviewProps) {
  const scopeKey = componentProjectId ?? 'all';
  const { availableGroups } = useGroup();
  const {
    data: projectFinancials,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['component-project-financials', scopeKey],
    queryFn: async (): Promise<ComponentProjectFinancials[]> => {
      if (componentProjectId) {
        return [await financialsApi.getComponentProjectFinancials(componentProjectId)];
      }
      return financialsApi.getAllComponentProjectFinancials();
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: 'always',
  });

  const labelById = useMemo(
    () => buildSubawardeeLabelLookup(availableGroups),
    [availableGroups]
  );

  const rollup = useMemo(() => {
    if (!projectFinancials?.length) return null;
    if (componentProjectId) {
      return buildProjectSpendRollup(projectFinancials[0]);
    }
    return buildAllProjectsRollup(projectFinancials);
  }, [componentProjectId, projectFinancials]);

  const atRiskSubawardees = useMemo(
    () =>
      rollup
        ? nameGivebackAtRiskSubawardees(
            rollup.atRisk,
            labelById,
            getSubawardeeDisplayLabel
          )
        : [],
    [labelById, rollup]
  );

  const title = componentProjectId
    ? `${getComponentProjectDisplayName(componentProjectId)} Spend`
    : 'Consortium Spend';
  const subtitle = componentProjectId
    ? 'Federal, TCF, and sub-awardee spend vs budgets'
    : 'Consortium-wide totals for federal, TCF, and sub-awardee spend vs budgets';

  if (!enabled) return null;

  const wrapWithCard = (content: ReactNode): JSX.Element =>
    showTitle ? (
      <Paper p="md" shadow="md" radius="md" sx={(theme) => projectCardPaperSx(theme)}>
        {content}
      </Paper>
    ) : (
      <>{content}</>
    );

  if (isLoading) {
    return wrapWithCard(
      <>
        {showTitle ? (
          <Stack spacing={4} mb="md">
            <Skeleton height={28} width={180} />
            <Skeleton height={14} width="55%" />
          </Stack>
        ) : null}
        <SimpleGrid
          cols={4}
          spacing="md"
          breakpoints={[
            { maxWidth: 'md', cols: 2 },
            { maxWidth: 'sm', cols: 1 },
          ]}
        >
          {Array.from({ length: 4 }).map((_, i) => (
            <Box
              key={i}
              p="md"
              sx={(theme) => ({
                borderRadius: theme.radius.md,
                backgroundColor:
                  theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
                border: `1px solid ${
                  theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[2]
                }`,
              })}
            >
              <Stack spacing="sm">
                <Skeleton height={16} width="55%" />
                <Skeleton height={28} width="70%" />
                <Skeleton height={12} width="45%" />
                <Skeleton height={8} radius="xl" mt={4} />
                <Group position="apart" noWrap>
                  <Skeleton height={14} width={70} />
                  <Skeleton height={12} width={90} />
                </Group>
              </Stack>
            </Box>
          ))}
        </SimpleGrid>
      </>
    );
  }

  if (error) {
    return (
      <Alert
        icon={<IconAlertCircle size={16} />}
        title="Error"
        color="red"
        role="alert"
      >
        Failed to load financials. Please try again later.
      </Alert>
    );
  }

  if (!rollup) return null;

  return wrapWithCard(
    <>
      {showTitle ? (
        <Stack spacing={4} mb="md">
          <Text size="lg" weight={700}>
            {title}
          </Text>
          <Text size="sm" color="dimmed">
            {subtitle}
          </Text>
        </Stack>
      ) : null}
      <SimpleGrid
        cols={rollup.hasProgram ? 4 : 3}
        spacing="md"
        breakpoints={[
          { maxWidth: 'md', cols: 2 },
          { maxWidth: 'sm', cols: 1 },
        ]}
      >
        <ProgramSpendCard
          label="Federal spend"
          value={rollup.federalSpendToDate}
          target={rollup.hasProgram ? rollup.federalBudget : rollup.federalTarget}
          tone="federal"
        />
        {rollup.hasProgram ? (
          <>
            <ProgramSpendCard
              label="TCF match"
              value={rollup.tcfMatchToDate}
              target={rollup.tcfMatchBudget}
              tone="tcf"
            />
            <ProgramSpendCard
              label="Sub-awardee match"
              value={rollup.matchSpendToDate}
              target={rollup.subawardeeMatchBudget}
              tone="match"
            />
          </>
        ) : (
          <ProgramSpendCard
            label="Match spend"
            value={rollup.matchSpendToDate}
            target={rollup.matchTarget}
            emptyLabel="No match spend reported yet"
            tone="match"
          />
        )}
        <GivebackAtRiskOverviewCard
          subawardeeCount={rollup.atRisk.count}
          dollars={rollup.atRisk.dollars}
          atRiskSubawardees={atRiskSubawardees}
        />
      </SimpleGrid>
    </>
  );
}
